import React from "react";
import * as LucideIcons from "lucide-react";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LANDING } from "@/constants/testIds";

// Canonical tier styling. Old ids (bronze/silver/gold/platinum) map to the
// same colours as their canonical equivalents so the site works while
// legacy subscribers still see their old plan name in dashboards.
const RING = {
  finder: "border-slate-300",
  seeker: "border-bronze/40",
  hunter: "border-silverTier/50",
  vault: "border-gold ring-2 ring-gold shadow-gold-glow",
  household: "border-primary ring-2 ring-primary shadow-teal-glow",
  bronze: "border-bronze/40",
  silver: "border-silverTier/50",
  gold: "border-gold ring-2 ring-gold shadow-gold-glow",
  platinum: "border-primary ring-2 ring-primary shadow-teal-glow",
};

function FeatureIcon({ name }) {
  if (!name) return <Check className="w-3 h-3" />;
  const Icon = LucideIcons[name];
  return Icon ? <Icon className="w-3.5 h-3.5" /> : <Check className="w-3 h-3" />;
}

export default function PlanCard({ plan, cycle, onSubscribe, ctaLabel = "Choose plan", loading = false }) {
  const price = plan.pricing?.[cycle] ?? plan.monthly_gbp;
  const cycleLabel = cycle === "monthly" ? "/mo" : cycle === "quarterly" ? "/quarter" : "/year";
  const isFree = price <= 0.001;
  const highlight = plan.id === "vault" || plan.id === "gold";
  const isFinder = plan.id === "finder";

  // Normalise feature objects → always {name, icon, description}
  const features = (plan.features || []).map((f) =>
    typeof f === "string" ? { name: f } : f
  );

  return (
    <div
      data-testid={LANDING.planCard(plan.id)}
      className={`relative bg-card border-2 ${RING[plan.id] || "border-border"} rounded-2xl p-7 flex flex-col transition-all duration-300 hover:-translate-y-1`}
    >
      {highlight && (
        <span className="absolute -top-3 left-6 bg-gold text-teal text-[10px] overline px-3 py-1 rounded-full font-bold">
          Most popular
        </span>
      )}
      {isFinder && (
        <span className="absolute -top-3 left-6 bg-primary text-primary-foreground text-[10px] overline px-3 py-1 rounded-full font-bold inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Start here — free
        </span>
      )}
      <div className="overline text-muted-foreground">{plan.name} tier</div>
      <div className="mt-3 flex items-baseline gap-1">
        {isFree ? (
          <>
            <span className="text-5xl font-display font-semibold tracking-tight">£0</span>
            <span className="text-sm text-muted-foreground">forever</span>
          </>
        ) : (
          <>
            <span className="text-5xl font-display font-semibold tracking-tight">£{price.toFixed(2)}</span>
            <span className="text-sm text-muted-foreground">{cycleLabel}</span>
          </>
        )}
      </div>
      {!isFree && cycle !== "monthly" && (
        <div className="text-xs text-muted-foreground mt-1">
          equiv. £{(price / (cycle === "quarterly" ? 3 : 12)).toFixed(2)} / month
        </div>
      )}
      <ul className="mt-6 space-y-3 flex-1">
        {features.map((f, i) => (
          <li key={f.code || `${plan.id}-${i}`} className="flex items-start gap-2 text-sm">
            <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <FeatureIcon name={f.icon} />
            </span>
            <span className="text-foreground/80" title={f.description || undefined}>{f.name}</span>
          </li>
        ))}
      </ul>
      <Button
        data-testid={LANDING.planSubscribeBtn(plan.id)}
        onClick={() => onSubscribe(plan.id)}
        disabled={loading}
        className={`mt-6 w-full ${highlight ? "bg-gold text-teal hover:bg-gold/90" : isFinder ? "bg-primary text-primary-foreground" : ""}`}
      >
        {loading ? "Redirecting…" : (isFree ? "Join Finder free" : ctaLabel)}
      </Button>
    </div>
  );
}
